import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Skeleton } from 'antd'
import moment from 'moment'

import getForCompleteCompanyDetails, {
  ForCompleteCompanyDetails,
} from 'core/application/adminDashboard/companySettings/companyDetails/getForCompleteCompanyDetails'
import { ProductIds } from 'core/application/commons/config'

import Item from 'common/components/dataDisplay/Item'
import Button from 'common/components/general/Button'

import { queryRequest } from 'common/RequestUtils'
import { parseZoneMonthYearFormat } from 'common/dateFormatters'

import useCurrentProduct from '../../../common/hooks/useCurrentProduct'
import { ROUTE_PATHS } from '../../../common/routePaths'

export const defaultCompanyDetailsValues: ForCompleteCompanyDetails = {
  name: '',
  ein: '',
  taxClassification: '',
  addressLine1: '',
  addressLine2: null,
  city: '',
  stateId: '',
  zipCode: '',
  mainPhoneNumber: '',
  groupHealthPlan: {
    startDate: moment(),
    insuranceCarrier: '',
    insurancePlan: '',
  },
}

const CompanyDetailsView: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [companyDetails, setCompanyDetails] = useState<ForCompleteCompanyDetails>(defaultCompanyDetailsValues)
  const { t: translate } = useTranslation()
  const currentProduct = useCurrentProduct()
  let history = useHistory()

  const fetchData = async () => {
    setLoading(true)
    let data = await queryRequest(() => getForCompleteCompanyDetails())
    setCompanyDetails(data)
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <div className="">
      <Row>
        <Col span={12} className="flex align-center text-semi-bold">
          {translate('admin-dashboard.company-settings.company-details.title')}
        </Col>
        <Col span={12} className="flex justify-end">
          <Button
            type="primary"
            onClick={() => {
              history.push(`${ROUTE_PATHS.COMPANY_SETTINGS}/edit`)
            }}
          >
            {translate('general.edit')}
          </Button>
        </Col>
      </Row>
      <Skeleton loading={loading}>
        <Row gutter={24}>
          <Col className="pt-24" xs={24} sm={12} lg={24} xl={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.company-name')}
              text={companyDetails.name}
            />
          </Col>
          <Col className="pt-24" xs={24} sm={12} lg={24} xl={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.ein')}
              text={companyDetails.ein}
            />
          </Col>
        </Row>

        {currentProduct.productId !== ProductIds.Perks && (
          <Row className="mt-24" gutter={24}>
            <Col xs={24}>
              <Item
                translate={translate}
                title={translate('admin-dashboard.company-settings.company-details.labels.tax-classification')}
                text={companyDetails.taxClassification}
              />
            </Col>
          </Row>
        )}

        <Row className="mt-40" gutter={24}>
          <Col xs={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.address-1')}
              text={companyDetails.addressLine1}
            />
          </Col>
          <Col xs={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.address-2')}
              text={companyDetails.addressLine2 ?? null}
            />
          </Col>
        </Row>

        <Row className="mt-24" gutter={24}>
          <Col xs={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.city')}
              text={companyDetails.city}
            />
          </Col>
          <Col xs={12}>
            <Row gutter={24}>
              <Col span={12}>
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.company-settings.company-details.labels.state')}
                  text={companyDetails.stateId}
                />
              </Col>
              <Col span={12}>
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.company-settings.company-details.labels.zip')}
                  text={companyDetails.zipCode}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="mt-40">
          <Col xs={24} sm={12}>
            <Item
              translate={translate}
              title={translate('admin-dashboard.company-settings.company-details.labels.phone')}
              text={companyDetails.mainPhoneNumber}
            />
          </Col>
        </Row>

        {currentProduct.productId === ProductIds.GroupHraId && (
          <>
            <Row className="mt-40">
              <Col xs={24} sm={12}>
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.company-settings.company-details.labels.start-month')}
                  text={
                    companyDetails.groupHealthPlan
                      ? parseZoneMonthYearFormat(companyDetails.groupHealthPlan.startDate)
                      : null
                  }
                />
              </Col>
            </Row>

            <Row className="mt-24" gutter={24}>
              <Col xs={12}>
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.company-settings.company-details.labels.insurance-carrier')}
                  text={companyDetails.groupHealthPlan ? companyDetails.groupHealthPlan.insuranceCarrier : null}
                />
              </Col>
              <Col xs={12}>
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.company-settings.company-details.labels.insurance-plan')}
                  text={companyDetails.groupHealthPlan ? companyDetails.groupHealthPlan.insurancePlan : null}
                />
              </Col>
            </Row>
          </>
        )}
      </Skeleton>
    </div>
  )
}

export default CompanyDetailsView
